import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateTask, useUpdateTask } from '@/hooks/use-tasks';
import { useProjects } from '@/hooks/use-projects';
import { toast } from '@/hooks/use-toast';
import { Switch } from '@/components/ui/switch';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Loader2, Repeat } from 'lucide-react';
import { FormField, useFieldValidation, validators } from '@/components/ui/form-field';
import { cn } from '@/lib/utils';

interface TaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId?: string;
  task?: any; // undefined = create mode, defined = edit mode
}

export function TaskDialog({ open, onOpenChange, projectId, task }: TaskDialogProps) {
  const createTask = useCreateTask();
  const updateTask = useUpdateTask();
  const { data: projectsData } = useProjects();
  const isEditing = !!task;
  const projects = projectsData?.data || [];

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [status, setStatus] = useState('todo');

  // Inline validation
  const titleValidation = useFieldValidation([validators.required('Task title is required')]);
  const [assigneeId, setAssigneeId] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState(projectId || '');
  const [dueDate, setDueDate] = useState('');
  const [estimatedHours, setEstimatedHours] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringFreq, setRecurringFreq] = useState('weekly');
  const [recurringInterval, setRecurringInterval] = useState(1);
  const [recurringDayOfWeek, setRecurringDayOfWeek] = useState('1'); // Monday default
  const [recurringDayOfMonth, setRecurringDayOfMonth] = useState('15');
  const [recurringEndCount, setRecurringEndCount] = useState('');

  useEffect(() => {
    if (task) {
      setTitle(task.title || '');
      titleValidation.setValue(task.title || '');
      setDescription(task.description || '');
      setPriority(task.priority || 'medium');
      setStatus(task.status || 'todo');
      setAssigneeId(task.assigneeId || '');
      setSelectedProjectId(task.projectId || projectId || '');
      setDueDate(task.dueDate ? task.dueDate.split('T')[0] : '');
      setEstimatedHours(task.estimatedHours?.toString() || '');
      setIsRecurring(task.isRecurring || false);
      if (task.recurringRule) {
        setRecurringFreq(task.recurringRule.freq || 'weekly');
        setRecurringInterval(task.recurringRule.interval || 1);
        setRecurringDayOfWeek(String(task.recurringRule.dayOfWeek ?? 1));
        setRecurringDayOfMonth(String(task.recurringRule.dayOfMonth ?? 15));
        setRecurringEndCount(task.recurringRule.count?.toString() || '');
      }
    } else {
      setTitle('');
      titleValidation.setValue('');
      setDescription('');
      setPriority('medium');
      setStatus('todo');
      setAssigneeId('');
      setSelectedProjectId(projectId || '');
      setDueDate('');
      setEstimatedHours('');
      setIsRecurring(false);
      setRecurringFreq('weekly');
      setRecurringInterval(1);
      setRecurringDayOfWeek('1');
      setRecurringDayOfMonth('15');
      setRecurringEndCount('');
    }
  }, [task, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate title before submission
    titleValidation.onBlur();
    if (titleValidation.error) {
      return;
    }

    // Build recurring rule
    let recurringRule = undefined;
    if (isRecurring) {
      const rule: any = {
        freq: recurringFreq,
        interval: recurringInterval || 1,
      };
      if (recurringFreq === 'weekly' && recurringDayOfWeek) {
        rule.dayOfWeek = parseInt(recurringDayOfWeek);
      }
      if (recurringFreq === 'monthly' && recurringDayOfMonth) {
        rule.dayOfMonth = parseInt(recurringDayOfMonth);
      }
      if (recurringEndCount) {
        rule.count = parseInt(recurringEndCount);
      }
      recurringRule = rule;
    }

    const data: any = {
      title,
      description: description || undefined,
      priority,
      status,
      assigneeId: assigneeId || undefined,
      dueDate: dueDate || undefined,
      estimatedHours: estimatedHours ? parseFloat(estimatedHours) : undefined,
      projectId: selectedProjectId || projectId || task?.projectId,
      isRecurring,
      recurringRule,
    };

    try {
      if (isEditing) {
        await updateTask.mutateAsync({ id: task.id, ...data });
        toast({ title: 'Task updated', description: `"${data.title}" has been updated.`, variant: 'success' });
      } else {
        await createTask.mutateAsync(data);
        toast({ title: 'Task created', description: `"${data.title}" has been created.`, variant: 'success' });
      }
      onOpenChange(false);
    } catch (err: any) {
      toast({ title: 'Failed to save task', description: err?.message || 'An unexpected error occurred.', variant: 'destructive' });
    }
  };

  const isPending = createTask.isPending || updateTask.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Task' : 'Create Task'}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Update the task details below.' : 'Add a new task to the project.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Project selector — only show when no project context */}
          {!projectId && !isEditing && (
            <div className="space-y-2">
              <Label>Project</Label>
              <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a project" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map((p: any) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Title with inline validation */}
          <FormField
            label="Title"
            error={titleValidation.error}
            touched={titleValidation.touched}
            dirty={titleValidation.dirty}
            showError={titleValidation.showError}
            isValid={titleValidation.isValid}
          >
            <Input
              id="title"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                titleValidation.onChange(e);
              }}
              onBlur={titleValidation.onBlur}
              placeholder="Enter task title"
              required
              className={cn(
                titleValidation.showError && 'border-red-400/50 focus:border-red-400 focus:ring-red-400',
                titleValidation.isValid && titleValidation.dirty && 'border-dyndev-green/50',
              )}
            />
          </FormField>

          <FormField label="Description">
            <RichTextEditor
              value={description}
              onChange={setDescription}
              placeholder="Optional description"
              minHeight="100px"
            />
          </FormField>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                  <SelectItem value="urgent">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todo">To Do</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="review">Review</SelectItem>
                  <SelectItem value="done">Done</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <FormField label="Due Date">
                <Input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                />
              </FormField>

              <FormField label="Est. Hours">
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  value={estimatedHours}
                  onChange={(e) => setEstimatedHours(e.target.value)}
                  placeholder="e.g. 4"
                />
              </FormField>
            </div>

            {/* Recurring toggle */}
            <div className="space-y-3 rounded-lg border border-[var(--border-color)] p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Repeat size={14} className="text-dyndev-green" />
                  <Label className="text-sm font-medium cursor-pointer">
                    Recurring Task
                  </Label>
                </div>
                <Switch
                  id="recurring-toggle"
                  checked={isRecurring}
                  onCheckedChange={setIsRecurring}
                />
              </div>

              {isRecurring && (
                <div className="space-y-3 pt-2 border-t border-[var(--border-color)]">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">Frequency</Label>
                      <Select value={recurringFreq} onValueChange={setRecurringFreq}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Every</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="1"
                          max="365"
                          value={recurringInterval}
                          onChange={(e) => setRecurringInterval(parseInt(e.target.value) || 1)}
                          className="w-16 text-center"
                        />
                        <span className="text-xs text-[var(--text-secondary)]">
                          {recurringFreq === 'daily' ? 'day(s)' : recurringFreq === 'weekly' ? 'week(s)' : 'month(s)'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {recurringFreq === 'weekly' && (
                    <div className="space-y-1.5">
                      <Label className="text-xs">On</Label>
                      <Select value={recurringDayOfWeek} onValueChange={setRecurringDayOfWeek}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">Sunday</SelectItem>
                          <SelectItem value="1">Monday</SelectItem>
                          <SelectItem value="2">Tuesday</SelectItem>
                          <SelectItem value="3">Wednesday</SelectItem>
                          <SelectItem value="4">Thursday</SelectItem>
                          <SelectItem value="5">Friday</SelectItem>
                          <SelectItem value="6">Saturday</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {recurringFreq === 'monthly' && (
                    <div className="space-y-1.5">
                      <Label className="text-xs">Day of Month</Label>
                      <Input
                        type="number"
                        min="1"
                        max="31"
                        value={recurringDayOfMonth}
                        onChange={(e) => setRecurringDayOfMonth(e.target.value)}
                        className="w-20 text-center"
                      />
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <Label className="text-xs">End after N occurrences (leave empty for no end)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="999"
                      value={recurringEndCount}
                      onChange={(e) => setRecurringEndCount(e.target.value)}
                      placeholder="∞ No end date"
                      className="w-24"
                    />
                  </div>
                </div>
              )}
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEditing ? 'Update Task' : 'Create Task'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
